import React, { useState } from 'react';
import Divider from '@mui/material/Divider';
import { Auth, Hub } from 'aws-amplify';

import { TabContext, TabPanel } from '@mui/lab';
import NN_logo from 'assets/icons/white/NN_logo.svg';
import DH_logo from 'assets/icons/white/DH_logo.svg';
import { useAlert } from 'components/message/AlertContext';
import {
    ResetPasswordForm,
    ChangePasswordForm,
    NewPasswordRequiredForm,
    SignInForm,
} from 'views/Auth/components';
import { CognitoUser } from '@aws-amplify/auth';
import { useAuthHeader } from 'views/Auth/hooks';
import config from '../../config';
import { NButton } from '@innersource-nn/novoui-react';

export enum Section {
    WELCOME = 'WELCOME',
    AUTH = 'AUTH',
    CHANGE_PASSWORD = 'CHANGE_PASSWORD',
    RESET_PASSWORD = 'RESET_PASSWORD',
    NEW_PASSWORD_REQUIRED = 'NEW_PASSWORD_REQUIRED',
}

export type ResetPasswordDetails = {
    attributeName: 'phone_number' | 'email';
    destination: string;
};

export default function AuthView() {
    const [section, setSection] = useState(Section.WELCOME);
    const [resetPasswordDetails, setResetPasswordDetails] =
        useState<ResetPasswordDetails>();
    const [username, setUsername] = useState('');
    const [user, setUser] = useState<CognitoUser>();

    const { openAlert } = useAlert();

    Hub.listen('auth', ({ payload: { event, data } }) => {
        switch (event) {
            case 'forgotPassword':
                setUsername(data.username);
                setSection(Section.CHANGE_PASSWORD);
                break;
            case 'forgotPasswordSubmit':
                openAlert({
                    message:
                        'You password has been updated. Please try login in again.',
                    severity: 'success',
                });
                break;
            default:
                break;
        }
    });

    const { title, subtitle } = useAuthHeader(section, resetPasswordDetails);

    const renderButton = () => {
        switch (section) {
            case Section.AUTH:
                return (
                    <>
                        <NButton
                            className="mt-8 w-full"
                            variant="outline"
                            onClick={() => {
                                setSection(Section.WELCOME);
                            }}
                        >
                            Cancel
                        </NButton>
                        <NButton
                            variant="text"
                            size="small"
                            onClick={() => {
                                setSection(Section.RESET_PASSWORD);
                            }}
                        >
                            Forgot the password?
                        </NButton>
                    </>
                );
            case Section.RESET_PASSWORD:
                return (
                    <NButton
                        variant="text"
                        size="small"
                        onClick={() => {
                            setSection(Section.AUTH);
                        }}
                    >
                        Cancel
                    </NButton>
                );
            case Section.CHANGE_PASSWORD:
                return (
                    <NButton
                        variant="text"
                        size="small"
                        onClick={() => {
                            setSection(Section.RESET_PASSWORD);
                        }}
                    >
                        Back
                    </NButton>
                );
        }
    };

    return (
        <div className="flex flex-col w-screen h-screen justify-center items-center bg-true-blue">
            <div className="rounded-xl bg-white px-48 pt-32 pb-24 text-center z-10 mt-[70px] w-[400px]">
                <img
                    className="mx-auto h-[72px] w-[72px]"
                    src={DH_logo}
                    alt="DH-logo"
                />
                <h1 className="mt-16 font-bold text-true-blue text-4xl pb-4">
                    {title}
                </h1>
                <h4 className="text-faded-blue-600 py-4 font-light text-sm mb-16">
                    {subtitle}
                </h4>
                <div className="flex flex-col gap-y-16">
                    {section === Section.WELCOME ? (
                        <>
                            <NButton
                                variant="fill"
                                size="small"
                                onClick={() =>
                                    Auth.federatedSignIn({
                                        customProvider: config.cognito.provider,
                                    })
                                }
                            >
                                Login with Okta
                            </NButton>
                            <Divider className="bg-granite-grey-200" />
                            <NButton
                                className="mb-8"
                                variant="outline"
                                size="small"
                                onClick={() => {
                                    setSection(Section.AUTH);
                                }}
                            >
                                Login with username and password
                            </NButton>
                        </>
                    ) : (
                        <TabContext value={section}>
                            <TabPanel
                                value={Section.AUTH}
                                style={{ padding: 0 }}
                            >
                                <SignInForm
                                    setSection={setSection}
                                    setUser={setUser}
                                />
                            </TabPanel>
                            <TabPanel
                                value={Section.RESET_PASSWORD}
                                style={{ padding: 0 }}
                            >
                                <ResetPasswordForm
                                    setResetPasswordDetails={
                                        setResetPasswordDetails
                                    }
                                />
                            </TabPanel>
                            <TabPanel
                                value={Section.CHANGE_PASSWORD}
                                style={{ padding: 0 }}
                            >
                                <ChangePasswordForm username={username} />
                            </TabPanel>
                            <TabPanel
                                value={Section.NEW_PASSWORD_REQUIRED}
                                style={{ padding: 0 }}
                            >
                                <NewPasswordRequiredForm user={user} />
                            </TabPanel>
                        </TabContext>
                    )}
                </div>
                {renderButton()}
            </div>
            <img className="pt-32" src={NN_logo} alt="NN-logo" />
        </div>
    );
}
